import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppSelector } from "utils/redux/hooks";
import {
  selectAllCompanyUsersById,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";
import { getUserName } from "utils/helperFunctions";
import {
  faChevronRight,
  faUserPlus,
  faEye,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { Dispatch, SetStateAction, useState } from "react";
import { selectDepartments } from "app/containers/AdminConsole/slice";
import { VISIBILITY_ICONS, VISIBILITY_OPTIONS_MAP } from "../../constants";
import { CustomVisibilityEntities, GoalVisibility } from "../../types";
import { cn } from "app/lib/utils";
import EntitySelectorDrawer from "./EntitySelectorDrawer";

interface GoalVisibilitySelectorProps {
  value: GoalVisibility;
  onChange: (value: GoalVisibility) => void;
  isOrgGoal?: boolean;
  showWarningForDowngrade?: boolean;
  onVisibilityWarning?: (newVisibility: GoalVisibility) => void;
  customVisibilityEntities: CustomVisibilityEntities;
  onChangeCustomVisibilityEntities: Dispatch<
    SetStateAction<CustomVisibilityEntities>
  >;
  goalsUserAccountId: number | null;
  availableVisibilityOptions?: GoalVisibility[];
}

export default function GoalVisibilitySelector({
  value,
  onChange,
  showWarningForDowngrade = false,
  onVisibilityWarning,
  customVisibilityEntities,
  onChangeCustomVisibilityEntities,
  goalsUserAccountId,
  availableVisibilityOptions = ["private", "standard", "shared"],
}: Readonly<GoalVisibilitySelectorProps>) {
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const departments = useAppSelector(selectDepartments);
  const teamsById = useAppSelector(selectTeamsByTeamId);
  const [isEntitySelectorDrawerOpen, setIsEntitySelectorDrawerOpen] =
    useState(false);

  const handleVisibilityChange = (newVisibility: GoalVisibility) => {
    if (showWarningForDowngrade && onVisibilityWarning) {
      if (value === "shared" && newVisibility !== "shared") {
        onVisibilityWarning(newVisibility);
        return;
      }
    }
    onChange(newVisibility);
  };

  const removeEntityFromState = (
    userAccountId: number,
    entityType: keyof CustomVisibilityEntities
  ) => {
    onChangeCustomVisibilityEntities((prev) => ({
      ...prev,
      [entityType]: prev[entityType].filter((id) => id !== userAccountId),
    }));
  };

  const getSelectCustomVisibilityUser = (
    entityId: number,
    entityType: keyof CustomVisibilityEntities
  ) => {
    let name = "";
    switch (entityType) {
      case "userAccountIds": {
        const user = usersById[entityId];
        if (!user) {
          return null;
        }
        name = getUserName(user);
        break;
      }
      case "teamIds": {
        const team = teamsById[entityId];
        if (!team) {
          return null;
        }
        name = team.teamName;
        break;
      }
      case "departmentIds": {
        const department = departments[entityId];
        if (!department) {
          return null;
        }
        name = department.name ?? "";
        break;
      }
    }

    return (
      <div
        key={`${entityId}-${entityType}`}
        className="flex items-center gap-2 rounded-full text-interactive-hover bg-interactive-fill px-[8px] py-[2px] text-sm align-center"
      >
        {name}
        <button
          className="no-style-button"
          onClick={() => {
            removeEntityFromState(entityId, entityType);
          }}
        >
          <FontAwesomeIcon icon={faXmark} className="me-1 text-xs" />
        </button>
      </div>
    );
  };

  const formatCustomEntities = () => {
    const parts: string[] = [];
    const userCount = customVisibilityEntities.userAccountIds.length;
    const teamCount = customVisibilityEntities.teamIds.length;
    const deptCount = customVisibilityEntities.departmentIds.length;

    if (userCount > 0)
      parts.push(`${userCount} ${userCount === 1 ? "user" : "users"}`);
    if (teamCount > 0)
      parts.push(`${teamCount} ${teamCount === 1 ? "team" : "teams"}`);
    if (deptCount > 0)
      parts.push(
        `${deptCount} ${deptCount === 1 ? "department" : "departments"}`
      );

    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return parts.join(" and ");
    return parts.slice(0, -1).join(", ") + ", and " + parts.at(-1);
  };

  const getVisibilityInfoBannerText = () => {
    const customEntitiesText = formatCustomEntities();
    const hasCustomEntities = customEntitiesText !== "";

    switch (value) {
      case "standard": {
        if (!hasCustomEntities) {
          return (
            <p>
              <strong>You, your manager, and admins</strong> can see this goal.
              It will appear in 1-on-1 prep and manager dashboards.
            </p>
          );
        }

        return (
          <p>
            <strong>You, your manager, and admins</strong> can see this goal.
            Also shared with {customEntitiesText}.
          </p>
        );
      }
      case "shared":
        return (
          <p>
            <strong>Everyone in your organization</strong> can see this goal.
            Others can link their goals to it for alignment.
          </p>
        );
      case "private":
      default: {
        if (!hasCustomEntities) {
          return (
            <p>
              <strong>Only you</strong> can see this goal. This is a private
              goal.
            </p>
          );
        }

        return (
          <p>
            <strong>Only you</strong> can see this goal. Also shared with{" "}
            {customEntitiesText}.
          </p>
        );
      }
    }
  };

  const getCustomVisibilitySelection = () => {
    return (
      <div>
        <p className="text-[length:var(--fontSize-label)] text-primary-text mb-sm">
          Also share with specific people or groups
        </p>
        <button
          onClick={() => setIsEntitySelectorDrawerOpen(true)}
          className="rounded border border-input bg-surface-1 px-[12px] py-[10px] text-[var(--text-placeholder)] d-flex gap-sm align-items-center hover:bg-surface-2 hover:border-strong w-full"
        >
          <FontAwesomeIcon icon={faUserPlus} />
          <p>Search all shared goals...</p>
          <FontAwesomeIcon icon={faChevronRight} className="ms-auto" />
        </button>
        <div className="flex flex-wrap gap-2 mt-sm">
          {customVisibilityEntities["userAccountIds"].map((id) =>
            getSelectCustomVisibilityUser(id, "userAccountIds")
          )}
          {customVisibilityEntities["teamIds"].map((id) =>
            getSelectCustomVisibilityUser(id, "teamIds")
          )}
          {customVisibilityEntities["departmentIds"].map((id) =>
            getSelectCustomVisibilityUser(id, "departmentIds")
          )}
        </div>
      </div>
    );
  };

  const getVisibilityModePicker = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-[16px]">
        {availableVisibilityOptions.map((opt) => (
          <button
            className={cn(
              "bg-white h-full border-[1px] px-[10px] py-[14px] rounded",
              value === opt ? "bg-interactive-bg border-interactive" : ""
            )}
            onClick={() => handleVisibilityChange(opt)}
            key={opt}
          >
            <div className="">
              <FontAwesomeIcon
                icon={VISIBILITY_ICONS[opt]}
                className={cn(
                  "text-2xl text-secondary",
                  value === opt ? "text-interactive" : ""
                )}
              />
            </div>
            <div>
              <p className="font-semibold">
                {VISIBILITY_OPTIONS_MAP[opt].title}
              </p>
              <p>{VISIBILITY_OPTIONS_MAP[opt].desc}</p>
            </div>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div>
      <p className="text-[length:var(--fontSize-form-prompt)] text-primary-text mb-sm">
        Who can see this goal?
      </p>
      <div className="rounded bg-interactive-bg border-[1px] border-interactive-fill py-[10px] px-[14px] d-flex align-items-center gap-2 mb-[12px]">
        <div className="goal-visibility-icon">
          <FontAwesomeIcon icon={faEye} className="w-4 h-4 shrink-0 mt-[1px]" />
        </div>
        <div className="goal-visibility-text">
          {getVisibilityInfoBannerText()}
        </div>
      </div>
      {getVisibilityModePicker()}
      {getCustomVisibilitySelection()}

      <EntitySelectorDrawer
        isOpen={isEntitySelectorDrawerOpen}
        onClose={() => {
          setIsEntitySelectorDrawerOpen(false);
        }}
        onSave={(customVisibilityEntities) => {
          onChangeCustomVisibilityEntities(customVisibilityEntities);
        }}
        preselectedCustomVisibilityEntities={customVisibilityEntities}
        excludedSelectableEntities={{
          userAccountIds: goalsUserAccountId ? [goalsUserAccountId] : [],
        }}
      />
    </div>
  );
}
